import * as React from 'react';
import styled from 'styled-components';
import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import Column from './Column';
import { QuestionOption } from '../../../models/QuestionModels';
import { answerSplitCharacter, dragUpToNumber, otherOptionValue } from '../../../core/constants';
import { singleColumnMaxWidth } from '../../../appComponents/cssConstants';

const ColumnContainer = (props: Props) => {
	const { specData, selectedValue, sourceColumnTitle, destinationColumnTitle } = props;
	const [isSourceColumnDragDisabled, setIsSourceColumnDragDisabled] = React.useState(
		selectedValue?.split(answerSplitCharacter).length >= dragUpToNumber
	);
	const initialData = createInitialData(specData, selectedValue, sourceColumnTitle, destinationColumnTitle);
	const { options, columnOrder } = initialData;
	const [columns, setColumns] = React.useState(initialData.columns);

	const handleDragEnd = (result: DropResult) => {
		const { destination, source, draggableId } = result;

		if (!destination) return;
		if (destination.droppableId === source.droppableId && destination.index === source.index) return;

		const start = columns[source.droppableId];
		const finish = columns[destination.droppableId];
		let updatedColumns = columns;

		if (start === finish) {
			// Order within same column
			const newOptionIds = Array.from(start.optionIds);
			newOptionIds.splice(source.index, 1);
			newOptionIds.splice(destination.index, 0, draggableId);
			const newColumn = {
				...start,
				optionIds: newOptionIds
			};
			updatedColumns = {
				...columns,
				[newColumn.id]: newColumn
			};
		} else {
			// Moving from one column to another
			const startOptionIds = Array.from(start.optionIds);
			startOptionIds.splice(source.index, 1);
			const newStart = {
				...start,
				optionIds: startOptionIds
			};
			const finishOptionIds = Array.from(finish.optionIds);
			finishOptionIds.splice(destination.index, 0, draggableId);
			const newFinish = {
				...finish,
				optionIds: finishOptionIds
			};
			updatedColumns = {
				...columns,
				[newStart.id]: newStart,
				[newFinish.id]: newFinish
			};
		}
		setColumns(updatedColumns);
		setIsSourceColumnDragDisabled(updatedColumns.destination.optionIds.length >= dragUpToNumber);
		const newValue = updatedColumns.destination.optionIds.join(answerSplitCharacter);
		props.onDragEnd(newValue);
	};

	return (
		<DragDropContext onDragEnd={handleDragEnd}>
			<Container>
				{columnOrder.map((columnId: string) => {
					const column = columns[columnId];
					const columnOptions = column.optionIds.map((optionId: string) => options[optionId]);
					return (
						<Column
							key={column.id}
							column={column}
							options={columnOptions}
							isDragDisabled={isSourceColumnDragDisabled && column.id === 'source'}
							onValueChange={props.onValueChange}
							floatToRight={column.id === 'destination'}
						/>
					);
				})}
			</Container>
		</DragDropContext>
	);
};

export default ColumnContainer;

interface Props {
	specData: QuestionOption<string | number>[];
	selectedValue: string;
	onDragEnd: (value: string) => void;
	onValueChange: (value: string) => void;
	sourceColumnTitle: string;
	destinationColumnTitle: string;
}

const createInitialData = (
	specData: QuestionOption<string | number>[],
	selectedValue: string,
	sourceColumnTitle: string,
	destinationColumnTitle: string
) => {
	const options = specData.reduce((map, obj) => {
		// eslint-disable-next-line no-param-reassign
		map[obj.value] = { id: obj.value, content: obj.label, showOtherTextField: obj.showOtherTextField };
		return map;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	}, {} as any);

	const selectedValueData = selectedValue.split(answerSplitCharacter).filter((x: string) => x !== '');
	let sourceColumnOptionIds = Object.keys(options).filter((x) => !selectedValueData.includes(x));
	const destinationColumnOptionIds = selectedValueData;
	const otherInputValue = selectedValueData?.find((x) => !specData.some((y) => y.value === x));
	if (otherInputValue) {
		sourceColumnOptionIds = sourceColumnOptionIds.filter((x) => x !== otherOptionValue);
		destinationColumnOptionIds.splice(selectedValueData.indexOf(otherInputValue), 1, otherOptionValue);
	}

	return {
		options,
		columns: {
			destination: {
				id: 'destination',
				title: destinationColumnTitle,
				optionIds: destinationColumnOptionIds
			},
			source: {
				id: 'source',
				title: sourceColumnTitle,
				optionIds: sourceColumnOptionIds
			}
		},
		columnOrder: ['destination', 'source']
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} as any;
};

const Container = styled.div`
	display: flex;

	@media (max-width: ${singleColumnMaxWidth}) {
		display: block;
	}
`;
